// Wizard step 7 — review checklist + funnel entry (FF-21 / MIS-1004).
//
// Renders EVERY check from FF-8's computed setup-status as a pass/fail row.
// Each failing row carries a "Fix" action that deep-links to the wizard step
// that completes it via the same `/funnels/new?domainId&step` search-param
// navigation the shell uses (1 domain, 2 dns, 3 landers, 4 integrations,
// 5 numbers, 6 binding, 7 review). When every check passes, an "Open funnel"
// link jumps to the live per-domain analytics page (FF-22).
//
// This consumes the SAME `['setup-status', domainId]` cache slot via FF-15's
// useSetupStatus — React Query dedupes against the wizard shell's query, so
// the checklist adds NO bespoke fetch.

import { Link, useNavigate } from '@tanstack/react-router';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { type DomainSetupStatus, useSetupStatus } from './useSetupStatus';
import type { WizardStepId } from './WizardNav';

/** One pass/fail line of the review checklist. */
export interface ChecklistRow {
  key: string;
  label: string;
  ok: boolean;
  /** The wizard step that fixes this check — the fail row's deep-link target. */
  step: WizardStepId;
  /** Optional human context (counts) shown next to the label. */
  detail?: string;
}

/**
 * Flatten the setup-status checks into ordered checklist rows. Exported for
 * tests; the order follows the wizard steps so failures read top-to-bottom as
 * "do this next".
 */
export function checklistRows(status: DomainSetupStatus): ChecklistRow[] {
  const { checks } = status;
  const perLander = checks.numberRouting.perLander;
  const routed = perLander.filter((l) => l.mode !== 'missing').length;
  return [
    { key: 'dns', label: 'DNS', ok: checks.dns, step: 2 },
    { key: 'ssl', label: 'SSL', ok: checks.ssl, step: 2 },
    {
      key: 'landers',
      label: 'Landers',
      ok: checks.landers.ok,
      step: 3,
      detail: `${checks.landers.count} lander${checks.landers.count === 1 ? '' : 's'}`,
    },
    // The setup-status pixel check (setup_status.ts) passes on domain.fbPixelId
    // OR a pixel id on EVERY lander — neither lives on step 4 (integrations =
    // CAPI/TrackDrive/postback creds). Step 3's "Facebook pixel ID" field is
    // the wizard surface that satisfies it (the domain-level id is set on the
    // domain settings page, outside the wizard).
    {
      key: 'pixel',
      label: 'Pixel',
      ok: checks.pixel,
      step: 3,
      detail: 'lander pixel override or domain-level pixel id',
    },
    { key: 'capiCreds', label: 'Meta CAPI', ok: checks.capiCreds, step: 4 },
    { key: 'trackdriveCreds', label: 'TrackDrive', ok: checks.trackdriveCreds, step: 4 },
    { key: 'postbackToken', label: 'Postback token', ok: checks.postbackToken, step: 4 },
    {
      key: 'numberRouting',
      label: 'Number routing',
      ok: checks.numberRouting.ok,
      step: 5,
      detail: `${routed}/${perLander.length} landers routed`,
    },
    {
      key: 'fbBinding',
      label: 'FB binding',
      ok: checks.fbBinding.ok,
      step: 6,
      detail: `${checks.fbBinding.boundLanders} bound`,
    },
  ];
}

export function ReviewChecklist({
  domainId,
}: {
  domainId: number | undefined;
}): React.ReactElement {
  const navigate = useNavigate();
  const { data: status, isLoading, isError } = useSetupStatus(domainId);

  // The shell's frontier gating normally keeps step 7 unreachable without a
  // domain; this guards direct renders (and narrows domainId for the links).
  if (domainId === undefined) {
    return (
      <p className="text-sm text-gray-500">
        Pick a domain in step 1 first — there is nothing to review yet.
      </p>
    );
  }
  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading setup status…</p>;
  }
  if (isError || !status) {
    return <p className="text-sm text-red-600">Failed to load setup status. Try again.</p>;
  }

  const rows = checklistRows(status);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-gray-500">
          {status.ready
            ? 'All checks pass — this funnel is ready to serve.'
            : 'Fix the failing checks below; each jumps back to its wizard step.'}
        </p>
        <Badge variant={status.ready ? 'green' : 'yellow'}>
          {status.completed}/{status.total} checks
        </Badge>
      </div>

      <ul
        aria-label="Setup checklist"
        className="overflow-hidden rounded-md border border-[var(--border-soft)]"
      >
        {rows.map((row) => (
          <li
            key={row.key}
            data-check={row.key}
            data-ok={row.ok}
            className="flex items-center gap-3 border-b border-[var(--border-soft)] px-4 py-3 last:border-b-0"
          >
            <span
              aria-hidden="true"
              className={
                row.ok
                  ? 'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgba(0,212,170,0.1)] text-xs font-semibold text-[var(--accent-light)]'
                  : 'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[var(--border-strong)] text-xs font-semibold text-[var(--muted)]'
              }
            >
              {row.ok ? '✓' : '○'}
            </span>
            <span className="text-sm font-medium text-gray-900">{row.label}</span>
            {row.detail ? <span className="text-xs text-gray-500">{row.detail}</span> : null}
            <span className="ml-auto">
              {row.ok ? (
                <Badge variant="green">Pass</Badge>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={`Fix ${row.label}`}
                  onClick={() =>
                    void navigate({
                      to: '/funnels/new',
                      search: { domainId, step: row.step },
                    })
                  }
                >
                  Fix
                </Button>
              )}
            </span>
          </li>
        ))}
      </ul>

      {status.ready ? (
        <div className="flex justify-end">
          <Link to="/domains/$domainId/analytics" params={{ domainId: String(domainId) }}>
            <Button>Open funnel</Button>
          </Link>
        </div>
      ) : null}
    </div>
  );
}
